import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Container,
  Form,
  Button,
  Table,
  Pagination,
  Row,
  Col,
} from "react-bootstrap";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import nodeApi from "../lib/api";

const ITEMS_PER_PAGE = 10;
const MAX_VISIBLE_PAGES = 5;

const Report = () => {
  const [pendingData, setPendingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("item_code"); // Default filter type
  const [challanType, setChallanType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["pendingReport"],
    queryFn: async () => {
      const [outgoingRes, incomingRes] = await Promise.all([
        nodeApi.get("/report/outgoing"),
        nodeApi.get("/report/incoming"),
      ]);
      return { outgoingData: outgoingRes.data, incomingData: incomingRes.data };
    },
  });

  useEffect(() => {
    if (data) {
      const { outgoingData, incomingData } = data;
      const outgoingMap = new Map();

      outgoingData.forEach((challan) => {
        const { outgoing_items, og_ch_no, og_ch_date, og_destination_name } =
          challan;
        outgoing_items.forEach((item) => {
          const key = `${item.item_code}_${item.size}_${og_ch_no}`;
          if (!outgoingMap.has(key)) {
            outgoingMap.set(key, {
              item_code: item.item_code,
              item_description: item.item_description,
              size: item.size,
              color_shade: item.og_color_shade,
              coater_name: og_destination_name,
              dispatched: 0,
              returned: 0,
              og_ch_no,
              og_ch_date,
              ic_ch_no: [],
              ic_ch_date: [],
              type: "Outgoing",
            });
          }
          outgoingMap.get(key).dispatched += item.quantity;
        });
      });

      incomingData.forEach((challan) => {
        const {
          incoming_items,
          ic_source_name,
          ic_ch_no,
          ic_ch_date,
          ref_og_ch_no,
        } = challan;
        incoming_items.forEach((item) => {
          const key = `${item.item_code}_${item.size}_${ref_og_ch_no}`;
          if (!outgoingMap.has(key)) {
            outgoingMap.set(key, {
              item_code: item.item_code,
              item_description: item.item_description,
              size: item.size,
              color_shade: item.ic_color_shade,
              coater_name: ic_source_name,
              dispatched: 0,
              returned: 0,
              og_ch_no: ref_og_ch_no,
              og_ch_date: "",
              ic_ch_no: [],
              ic_ch_date: [],
              type: "Incoming",
            });
          }
          const entry = outgoingMap.get(key);
          entry.returned += item.quantity;
          if (!entry.ic_ch_no.includes(ic_ch_no)) {
            entry.ic_ch_no.push(ic_ch_no);
            entry.ic_ch_date.push(ic_ch_date);
          }
          if (entry.type === "Outgoing") entry.type = "Both";
        });
      });

      const finalList = Array.from(outgoingMap.values()).map((entry) => ({
        ...entry,
        pending: entry.dispatched - entry.returned,
      }));

      setPendingData(finalList);
    }
  }, [data]);

  useEffect(() => {
    let filtered = [...pendingData];

    // Apply search filter based on filterType
    if (searchQuery && filterType) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const value =
          filterType === "ic_ch_no"
            ? item[filterType].join(", ")
            : item[filterType];
        return value?.toString().toLowerCase().includes(query);
      });
    }

    // Apply challan type filter
    if (challanType) {
      filtered = filtered.filter((item) =>
        challanType === "Both"
          ? item.type === "Both"
          : item.type === challanType || item.type === "Both"
      );
    }

    // Apply date filter
    if (startDate || endDate) {
      const startStr = startDate
        ? new Date(startDate).toISOString().split("T")[0]
        : null;
      const endStr = endDate
        ? new Date(endDate).toISOString().split("T")[0]
        : null;

      filtered = filtered.filter((item) => {
        const rawDate =
          challanType === "Incoming" ? item.ic_ch_date[0] : item.og_ch_date;
        if (!rawDate) return false;
        const challanDateStr = new Date(rawDate).toISOString().split("T")[0];
        if (startStr && challanDateStr < startStr) return false;
        if (endStr && challanDateStr > endStr) return false;
        return true;
      });
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchQuery, filterType, challanType, startDate, endDate, pendingData]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getVisiblePages = () => {
    const pages = [];
    let startPage = Math.max(
      1,
      currentPage - Math.floor(MAX_VISIBLE_PAGES / 2)
    );
    let endPage = startPage + MAX_VISIBLE_PAGES - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - MAX_VISIBLE_PAGES + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="d-flex flex-column vh-100">
      <Navbar />
      <div className="d-flex flex-grow-1 overflow-hidden">
        <Sidebar />
        <Container fluid className="p-4 d-flex flex-column overflow-hidden">
          <h4 className="mb-3 fw-bold">Pending Reports</h4>

          {/* Filters */}
          <Row className="mb-3 align-items-end">
            <Col xs={4} md={2}>
              <Form.Group>
                <Form.Label className="small mb-1">Filter By</Form.Label>
                <Form.Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="item_code">Supplier Item Code</option>
                  <option value="item_description">Item Description</option>
                  <option value="coater_name">Coater Name</option>
                  <option value="og_ch_no">OG Challan No</option>
                  <option value="ic_ch_no">IC Challan No</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={8} md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Search</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small mb-1">Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small mb-1">End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={1}>
              {(startDate || endDate || searchQuery || challanType) && (
                <Button
                  variant="outline-danger"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setSearchQuery("");
                    setChallanType("");
                    setFilterType("item_code");
                  }}
                >
                  Clear
                </Button>
              )}
            </Col>
          </Row>

         <style>
  {`
    .table-wrapper {
      overflow-x: auto;
      max-width: 100%;
      border: 1px solid #ddd;
    }

    .custom-table {
      width: max-content;
      table-layout: fixed;
    }

    .custom-table th,
    .custom-table td {
      padding: 8px 8px;
      line-height: 1.5;
      text-align: center;
      border: 1px solid #ddd;
      white-space: normal;  /* allow wrapping */
      word-wrap: break-word; /* break long words */
    }

    /* Fixed widths for each column */
    .col-item-code { width: 100px; }
    .col-item-desc { width: 200px; }
    .col-size { width: 80px; }
    .col-color { width: 120px; }
    .col-coater { width: 150px; }
    .col-og-no { width: 150px; }
    .col-og-date { width: 150px; }
    .col-ic-no { width: 120px; }
    .col-ic-date { width: 150px; }
    .col-dispatched { width: 150px; }
    .col-returned { width: 150px; }
    .col-pending { width: 150px; }
  `}
        </style>



          {/* Table */}
          <div className="table-wrapper">
            <Table bordered hover size="sm" className="mb-0 custom-table ">
              <thead className="table-secondary sticky-top">
                <tr>
                    <th className="col-item-code">Supplier Item Code</th>
                    <th className="col-item-desc">Item Description</th>
                    <th className="col-size">Size</th>
                    <th className="col-color">Color Shade</th>
                    <th className="col-coater">Coater Name</th>
                    <th className="col-og-no">OG Challan No</th>
                    <th className="col-og-date">OG Challan Date</th>
                    <th className="col-ic-no">IC Challan No</th>
                    <th className="col-ic-date">IC Challan Date</th>
                    <th className="col-dispatched">Dispatched Qty</th>
                    <th className="col-returned">Returned Qty</th>
                    <th className="col-pending">Pending Qty</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="12" className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td colSpan="12" className="text-center text-danger">
                      Error: {error.message}
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.item_code}</td>
                      <td>{item.item_description}</td>
                      <td>{item.size}</td>
                      <td>{item.color_shade}</td>
                      <td>{item.coater_name}</td>
                      <td>{item.og_ch_no}</td>
                      <td>
                        {item.og_ch_date
                          ? new Date(item.og_ch_date).toLocaleDateString(
                              "en-GB"
                            )
                          : "-"}
                      </td>
                      <td>{item.ic_ch_no.join(", ") || "-"}</td>
                      <td>
                        {item.ic_ch_date
                          .map((d) =>
                            d ? new Date(d).toLocaleDateString("en-GB") : "-"
                          )
                          .join(", ") || "-"}
                      </td>
                      <td>{item.dispatched}</td>
                      <td>{item.returned}</td>
                      <td>{item.pending}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className="text-center">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              className="justify-content-center py-2 bg-white"
              style={{ position: "sticky", bottom: 0, zIndex: 10 }}
            >
              <Pagination.Prev
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </Pagination.Prev>
              {getVisiblePages().map((page) => (
                <Pagination.Item
                  key={page}
                  active={currentPage === page}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Pagination.Next>
            </Pagination>
          )}
        </Container>
      </div>
    </div>
  );
};

export default Report;
